import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { representativeSchema, type RepresentativeFormData } from './representative-schema';
import { useCreateRepresentative, useUpdateRepresentative } from '@/hooks/use-representatives';
import type { Representative } from '@erp/data-access/interfaces';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Button, Input, Label, Textarea,
} from '@erp/ui';

interface RepresentativeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  representative?: Representative | null;
  onCreated?: (rep: Representative) => void;
}

export function RepresentativeFormDialog({
  open, onOpenChange, representative, onCreated,
}: RepresentativeFormDialogProps) {
  const { t } = useTranslation();
  const isEdit = !!representative;
  const createMutation = useCreateRepresentative();
  const updateMutation = useUpdateRepresentative();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register, handleSubmit, reset,
    formState: { errors, isSubmitting },
  } = useForm<RepresentativeFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Zod v4 + RHF resolver compat
    resolver: zodResolver(representativeSchema) as any,
    defaultValues: { name: '', phone: '', email: '', notes: '', commissionRate: 0 },
  });

  useEffect(() => {
    if (representative) {
      reset({
        name: representative.name,
        phone: representative.phone ?? '',
        email: representative.email ?? '',
        notes: representative.notes ?? '',
        commissionRate: representative.commissionRate ?? 0,
      });
    } else {
      reset({ name: '', phone: '', email: '', notes: '', commissionRate: 0 });
    }
    setSubmitError(null);
  }, [representative, reset, open]);

  const onSubmit = async (data: RepresentativeFormData) => {
    setSubmitError(null);
    const input = {
      name: data.name,
      phone: data.phone || undefined,
      email: data.email || undefined,
      notes: data.notes || undefined,
      commissionRate: data.commissionRate,
    };
    try {
      if (isEdit && representative) {
        await updateMutation.mutateAsync({ id: representative.id, input });
      } else {
        const created = await createMutation.mutateAsync(input);
        onCreated?.(created);
      }
      onOpenChange(false);
    } catch (e) {
      const err = e as { message?: string };
      setSubmitError(err?.message ?? t('common.errorSaving'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('representatives.editRepresentative') : t('representatives.newRepresentative')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">{t('representatives.name')} *</Label>
            <Input id="name" {...register('name')} autoFocus />
            {errors.name && <p className="text-xs text-destructive">{t(errors.name.message!)}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="phone">{t('representatives.phone')}</Label>
              <Input id="phone" {...register('phone')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">{t('representatives.email')}</Label>
              <Input id="email" type="email" {...register('email')} />
              {errors.email && (
                <p className="text-xs text-destructive">{t(errors.email.message!)}</p>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="commissionRate">{t('representatives.commissionRate')} (%)</Label>
            <Input
              id="commissionRate"
              type="number"
              step="0.01"
              min={0}
              max={100}
              {...register('commissionRate', { valueAsNumber: true })}
            />
            <p className="text-xs text-muted-foreground">
              {t('representatives.commissionRateHint')}
            </p>
            {errors.commissionRate && (
              <p className="text-xs text-destructive">{t(errors.commissionRate.message!)}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes">{t('representatives.notes')}</Label>
            <Textarea id="notes" {...register('notes')} rows={2} />
          </div>

          {submitError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {submitError}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
